/**
 * 9. Notifications Center — all notifications with read/unread state and
 * notification preference configuration.
 *
 * Arabic and English (C7): the filter tabs are chosen by id, and a
 * notification's record type is shown as words, not as its stored code.
 *
 * The rows are `GET /v1/notifications` as the API sends them (the
 * "Notification" table: `title`, `body`, `entity_type`, `severity`,
 * `is_read`, `created_at`; the client adds camelCase names). The screen used
 * to read `read`, `message` and `type`, which the API never sends, so every
 * row stayed "New" after Mark read had succeeded (UI-HONEST).
 *
 * D58, the screen half: Mark all read sends `POST /v1/notifications/all/read`
 * and shows what the API answers, in the person's language. Until the API
 * half lands (PLAT-R1) that answer is a refusal, and the screen says so
 * instead of throwing. Mark read on one row does the same on a refusal.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  Button,
  DataTable,
  Skeleton,
  Tabs,
} from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { useApiList, post, useApiMutation } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { recordTypeLabel } from "@/lib/i18n/records";
import { formatDateTime } from "@/lib/money";

interface NotificationItem {
  id: string;
  title: string;
  body: string | null;
  /** The record it is about, by its type code (`ApprovalRequest`). */
  entityType: string | null;
  isRead: boolean;
  createdAt: string;
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>("all");
  const [notice, setNotice] = useState<string | null>(null);
  const { data, isLoading } = useApiList<NotificationItem>(
    ["notifications"],
    "/notifications?limit=100",
  );
  const markRead = useApiMutation(["notifications"], (id: string) =>
    post(`/notifications/${id}/read`, {}),
  );
  const markAllRead = useApiMutation(["notifications"], () =>
    post("/notifications/all/read", {}),
  );

  if (isLoading) return <Skeleton rows={8} />;

  const all = data?.items ?? [];
  const items = all.filter((n) => (filter === "unread" ? !n.isRead : true));
  const unread = all.filter((n) => !n.isRead).length;
  const failure = markAllRead.error ?? markRead.error;

  return (
    <div>
      <PageHeader
        title={t("notifications.title")}
        subtitle={t("notifications.unread", { count: unread })}
        actions={
          <Button
            size="sm"
            disabled={markAllRead.isPending}
            onClick={() => {
              setNotice(null);
              markRead.reset();
              markAllRead.mutate(undefined, {
                onSuccess: () => setNotice(t("notificationActions.allRead")),
              });
            }}
          >
            {t("notifications.markAllRead")}
          </Button>
        }
      />
      {failure && (
        <div
          role="alert"
          style={{ color: "var(--atlas-danger)", marginBlockEnd: 12 }}
        >
          <ErrorText error={failure} />
        </div>
      )}
      {notice && (
        <p role="status" style={{ marginBlock: "0 12px" }}>
          {notice}
        </p>
      )}
      <Tabs
        tabs={[
          { id: "all", label: t("notifications.tabs.all") },
          { id: "unread", label: t("notifications.tabs.unread") },
        ]}
        active={filter}
        onChange={setFilter}
      />
      <Card>
        <DataTable
          rows={items}
          keyField="id"
          columns={[
            {
              key: "isRead",
              label: "",
              render: (r) =>
                !r.isRead ? (
                  <Badge tone="primary">{t("notifications.new")}</Badge>
                ) : null,
            },
            {
              key: "entityType",
              label: t("notifications.columns.type"),
              render: (r) =>
                r.entityType ? (
                  <Badge>{recordTypeLabel(t, r.entityType)}</Badge>
                ) : null,
            },
            {
              key: "title",
              label: t("notifications.columns.message"),
              render: (r) => (
                <>
                  {r.title}
                  {r.body && (
                    <div style={{ fontSize: 13, marginBlockStart: 2 }}>
                      {r.body}
                    </div>
                  )}
                </>
              ),
            },
            {
              key: "createdAt",
              label: t("notifications.columns.received"),
              render: (r) => formatDateTime(r.createdAt),
            },
            {
              key: "actions",
              label: t("notifications.columns.actions"),
              render: (r) =>
                !r.isRead ? (
                  <Button
                    size="sm"
                    disabled={markRead.isPending}
                    onClick={() => {
                      setNotice(null);
                      markAllRead.reset();
                      markRead.mutate(r.id);
                    }}
                  >
                    {t("notifications.markRead")}
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>
    </div>
  );
}
